import { routes, RouteMenuLocationEnum, RouteMenuSectionEnum } from '@config/routes';
import { UserGrantActionToken } from '@modules/types/graphql';

import type { Route } from '@config/routes';
import type { UserEntity } from '@modules/user/entities';

type GetMenuRoutesOptions = {
    forUser: UserEntity;
    forSection?: RouteMenuSectionEnum;
    orderByOriented?: 'lap-first' | 'regional-center-first';
};

const filterBySection = (routes: Route[], section: RouteMenuSectionEnum) =>
    routes.filter(route => route.menuSections?.includes(section));

export const getMenuRoutes = (
    location: RouteMenuLocationEnum,
    options?: GetMenuRoutesOptions,
): Route[] => {
    let menuRoutes = Object.values(routes).filter(
        route =>
            typeof route.menuLocations !== 'undefined' && route.menuLocations.includes(location),
    );

    if (options?.forUser) {
        const currentRole = options.forUser.getCurrentRole();
        const { categories, actions } = options.forUser.getAllowedRoutes();

        menuRoutes = menuRoutes
            .filter(route => {
                let needCategoryGrant = route.categoryGrant;
                let needActionGrants = route.actionGrants;
                if (currentRole && route.categoryGrantFor?.[currentRole]) {
                    needCategoryGrant = route.categoryGrantFor[currentRole];
                }

                if (!needCategoryGrant) {
                    return true;
                }

                const hasAccessByCategory = categories.includes(needCategoryGrant);

                if (!!needActionGrants && needActionGrants.length > 0) {
                    const actionsByCategory = actions[needCategoryGrant];
                    const hasAllGrants =
                        actionsByCategory?.length === 1 &&
                        actionsByCategory[0] === UserGrantActionToken.all;

                    const hasAccessByActions = actions[needCategoryGrant]?.some(action =>
                        needActionGrants?.includes(action),
                    );

                    return hasAllGrants || hasAccessByActions;
                }

                return hasAccessByCategory;
            })
            .filter(route => {
                if (!route.roles || !currentRole) {
                    return true;
                }

                return route.roles.includes(currentRole);
            });
    }

    if (options?.forSection) {
        menuRoutes = filterBySection(menuRoutes, options.forSection);
    }

    if (options?.orderByOriented) {
        const sortRule = options.orderByOriented;

        const lapOrientedRoutes = filterBySection(menuRoutes, RouteMenuSectionEnum.lapDataOriented);
        const regionalCenterOrientedRoutes = filterBySection(
            menuRoutes,
            RouteMenuSectionEnum.regionalCenterDataOriented,
        );

        if (sortRule === 'lap-first') {
            menuRoutes = [...lapOrientedRoutes, ...regionalCenterOrientedRoutes];
        }

        if (sortRule === 'regional-center-first') {
            menuRoutes = [...regionalCenterOrientedRoutes, ...lapOrientedRoutes];
        }
    }

    return menuRoutes;
};
